/**
 * Intercom / Fin helpers (PAY-2699).
 *
 * The Messenger is loaded by Segment's Intercom destination
 * (`cdn.segment.com/.../integrations/intercom/.../intercom.dynamic.js`), not
 * this repo.
 * Pay's Support entry is `#assignar-pay-support-button` — Intercom binds that
 * as `custom_launcher_selector`. The default Fin bubble must stay hidden so
 * it does not overlay the sidebar profile.
 */

export type IntercomCommand = ((
  command: string,
  ...args: unknown[]
) => void) & {
  /** Set by the real Messenger once it has booted; absent on the loader stub. */
  booted?: boolean;
};

export type IntercomSettings = {
  hide_default_launcher?: boolean;
  [key: string]: unknown;
};

export type IntercomHost = {
  Intercom?: IntercomCommand;
  intercomSettings?: IntercomSettings;
  setInterval: Window['setInterval'];
  clearInterval: Window['clearInterval'];
  setTimeout: Window['setTimeout'];
  clearTimeout: Window['clearTimeout'];
};

declare global {
  interface Window {
    Intercom?: IntercomCommand;
    intercomSettings?: IntercomSettings;
  }
}

export const INTERCOM_LAUNCHER_POLL_MS = 250;
export const INTERCOM_LAUNCHER_GIVE_UP_MS = 30_000;

export function hideIntercomDefaultLauncher(host: IntercomHost = window): void {
  host.intercomSettings = {
    ...host.intercomSettings,
    hide_default_launcher: true,
  };

  host.Intercom?.('update', { hide_default_launcher: true });
}

/**
 * Hide the default launcher as soon as Intercom is present. Segment loads the
 * widget asynchronously, so we poll briefly and re-apply on messenger close
 * in case a later `update`/`identify` restores the bubble.
 */
export function watchAndHideIntercomLauncher(
  host: IntercomHost = window,
): () => void {
  hideIntercomDefaultLauncher(host);

  if (host.Intercom) {
    host.Intercom('onHide', () => hideIntercomDefaultLauncher(host));
    return () => undefined;
  }

  let timeoutId: ReturnType<Window['setTimeout']> | undefined;

  const intervalId = host.setInterval(() => {
    hideIntercomDefaultLauncher(host);

    if (host.Intercom) {
      host.Intercom('onHide', () => hideIntercomDefaultLauncher(host));
      host.clearInterval(intervalId);
      if (timeoutId !== undefined) {
        host.clearTimeout(timeoutId);
      }
    }
  }, INTERCOM_LAUNCHER_POLL_MS);

  timeoutId = host.setTimeout(() => {
    host.clearInterval(intervalId);
  }, INTERCOM_LAUNCHER_GIVE_UP_MS);

  return () => {
    host.clearInterval(intervalId);
    if (timeoutId !== undefined) {
      host.clearTimeout(timeoutId);
    }
  };
}

/**
 * PAY-604: Milo → Fin handoff transport.
 *
 * Intercom is loaded by Segment, asynchronously, so the Messenger can be
 * missing for two very different reasons and the handoff has to tell them
 * apart:
 *
 *  - it has not booted YET (still loading) — wait briefly, then proceed;
 *  - it is never coming (ad-blocker, Intercom outage, a proxy blocking
 *    `widget.intercom.io`) — the caller must show a fallback instead of a
 *    dead button.
 *
 * `window.Intercom` EXISTING is not the signal. Segment installs a queueing
 * stub (`window.Intercom = function () { window.Intercom.q.push(arguments) }`)
 * BEFORE it loads the widget, so when only the widget is blocked the stub is
 * there forever and every command sits in its queue: Milo would close and Fin
 * would never open. The real Messenger sets `booted`, so that is what
 * {@link isIntercomReady} checks.
 *
 * {@link waitForIntercom} collapses both into one answer. Intercom documents a
 * race where a `startConversation` / `showNewMessage` issued before the
 * Messenger is ready is silently DROPPED, which is exactly the failure this
 * guard exists to prevent — a user who clicks Support, watches Milo close, and
 * lands in an empty Fin window with their question gone.
 */
export const INTERCOM_READY_POLL_MS = 100;
export const INTERCOM_READY_TIMEOUT_MS = 5_000;

/** True once the real Messenger has booted, not merely the loader stub. */
export function isIntercomReady(host: IntercomHost): boolean {
  return Boolean(host.Intercom && host.Intercom.booted === true);
}

/**
 * Resolve with the Intercom command function once the Messenger has BOOTED,
 * or `null` when it has not within {@link INTERCOM_READY_TIMEOUT_MS} — which
 * includes a loader stub that never boots.
 *
 * Resolves SYNCHRONOUSLY-fast (one microtask) in the overwhelmingly common
 * case where Intercom booted long before the user clicked anything.
 */
export function waitForIntercom(
  host: IntercomHost = window,
  timeoutMs: number = INTERCOM_READY_TIMEOUT_MS,
): Promise<IntercomCommand | null> {
  if (isIntercomReady(host)) {
    return Promise.resolve(host.Intercom ?? null);
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value: IntercomCommand | null) => {
      if (settled) return;
      settled = true;
      host.clearInterval(intervalId);
      host.clearTimeout(timeoutId);
      resolve(value);
    };

    const intervalId = host.setInterval(() => {
      if (isIntercomReady(host)) {
        finish(host.Intercom ?? null);
      }
    }, INTERCOM_READY_POLL_MS);

    const timeoutId = host.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Open the Messenger on a NEW conversation whose opening message is `message`,
 * sent immediately and attributed to the user.
 *
 * `startConversation` (auto-send) is deliberate, and it is the reason the
 * seeded text must be a plain first-person question: Intercom attributes the
 * message to the USER, and Fin runs semantic retrieval over help-centre
 * content against it. An API-initiated conversation also opens straight to the
 * user's message — Fin's configured greeting never fires — so any
 * meta-narration ("I asked Milo and it said…") would be text that appears in
 * no help article and can only dilute the match.
 */
export function startIntercomConversation(
  message: string,
  host: IntercomHost = window,
): boolean {
  if (!host.Intercom) {
    return false;
  }

  host.Intercom('startConversation', message);
  return true;
}

/**
 * Stamp custom attributes on the Intercom user record.
 *
 * The "came from Milo" provenance rides here rather than in the message body:
 * it keeps Fin's retrieval input clean (see {@link startIntercomConversation})
 * while still giving support-handoff analytics for free on the Intercom side.
 */
export function updateIntercomUserAttributes(
  attributes: Record<string, unknown>,
  host: IntercomHost = window,
): boolean {
  if (!host.Intercom) {
    return false;
  }

  host.Intercom('update', attributes);
  return true;
}

/**
 * End the Messenger session on sign-out (PAY-3983).
 *
 * Intercom keeps its own session cookie, so without this the next person to
 * sign in on the same browser opens the previous user's conversations. Segment
 * cannot do it for us: `analytics.reset()` clears only Segment's own state.
 *
 * A loader stub that never booted queues the call, which is harmless; with no
 * `Intercom` at all there is no session to end.
 */
export function shutdownIntercom(host: IntercomHost = window): boolean {
  if (!host.Intercom) {
    return false;
  }

  // Runs inside the auth listener on sign-out. A throw from Intercom's script
  // must not cost the user their sign-out.
  try {
    host.Intercom('shutdown');
  } catch {
    return false;
  }

  return true;
}
